import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
  Tag,
  Space,
  Tooltip,
  DatePicker,
  Alert,
} from "antd";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
  CalendarOutlined,
  DollarCircleOutlined,
  EnvironmentOutlined,
  AimOutlined,
  AliwangwangOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import GoogleMapReact from "google-map-react";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Marker component for the map
const MapMarker = ({ text }) => (
  <div
    style={{
      position: "absolute",
      transform: "translate(-50%, -100%)",
      color: "#1890ff",
      fontSize: "32px",
      cursor: "pointer",
    }}
  >
    📍
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
        backgroundColor: "white",
        padding: "4px 12px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: "bold",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        border: "1px solid #d9d9d9",
      }}
    >
      {text}
    </div>
  </div>
);

const Vendors = () => {
  const { request } = useRequest();
  const navigate = useNavigate();

  // UI State
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [subscriptionForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] =
    useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showPeriodFields, setShowPeriodFields] = useState(false);
  const [showTrialFields, setShowTrialFields] = useState(false);

  // NEW: Location and Map State
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [tempLocation, setTempLocation] = useState({
    lat: 40.7128, // Default values instead of null
    lng: -74.006,
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
  });
  const [mapCenter, setMapCenter] = useState({
    lat: 40.7128,
    lng: -74.006,
  });
  const [mapZoom, setMapZoom] = useState(12);
  const [geocoder, setGeocoder] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState("");

  // Google Maps API Key - Replace with your own
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "AIzaSyDQ2DCe8qS4qVCkMtRZRnZZ_TF2qq1HSvs";

  // Subscription status options
  const subscriptionOptions = [
    { value: "Trial", label: "Trial", color: "blue" },
    { value: "Active", label: "Active", color: "green" },
    { value: "Cancelled", label: "Cancelled", color: "red" },
    { value: "Expired", label: "Expired", color: "orange" },
  ];

  // Period unit options
  const periodUnitOptions = [
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
  ];

  // Fetch Vendors
  const fetchUsers = (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    let qp = `page=${page}&page_size=${pageSize}&role=Gym Owner`;
    if (filters.searchKeyword?.trim())
      qp += `&search=${encodeURIComponent(filters.searchKeyword.trim())}`;
    if (filters.status && filters.status !== "all")
      qp += `&status=${filters.status}`;
    if (filters.subscriptionStatus && filters.subscriptionStatus !== "all")
      qp += `&subscription_status=${filters.subscriptionStatus}`;
    if (filters.startDate && filters.endDate) {
      qp += `&start_date=${filters.startDate}&end_date=${filters.endDate}`;
    }

    request({
      url: `${apiPath.vendorList}?${qp}`,
      method: "GET",
      onSuccess: (res) => {
        setUsers(res?.data || []);
        setPagination({
          current: res?.pagination?.current_page || 1,
          pageSize: res?.pagination?.page_size || 10,
          total: res?.pagination?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch vendors", Severty.ERROR);
      },
    });
  };

  // Fetch Single Vendor
  const fetchUserDetails = (id) => {
    setLoading(true);
    request({
      url: `/admin/get_user?id=${id}`,
      method: "GET",
      onSuccess: (res) => {
        if (res.status) {
          setViewingUser(res.data);
          setViewModalVisible(true);
        }
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const handleVendorDashboard = (id) => {
    navigate(`/vendors/shadow-view/${id}`);
  };

  // Initial Load
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle Google Maps load
  const handleApiLoaded = (map, maps) => {
    setMapsLoaded(true);
    setGeocoder(new maps.Geocoder());
  };

  // NEW: Open Location Modal - UPDATED with safe parsing
  const openLocationModal = () => {
    const currentValues = form.getFieldsValue();

    // Safely parse latitude and longitude values
    const parseCoordinate = (value) => {
      if (value === null || value === undefined || value === "") return 40.7128;
      const num = parseFloat(value);
      return isNaN(num) ? 40.7128 : num;
    };

    const currentLat = parseCoordinate(currentValues.latitude);
    const currentLng = parseCoordinate(currentValues.longitude);

    setTempLocation({
      lat: currentLat,
      lng: currentLng,
      address: currentValues.location || "",
      city: currentValues.city || "",
      state: currentValues.state || "",
      country: currentValues.country || "",
      postal_code: currentValues.postal_code || "",
    });

    setMapCenter({
      lat: currentLat,
      lng: currentLng,
    });
    setLocationModalVisible(true);
  };

  // NEW: Handle map click - UPDATED with type safety
  const handleMapClick = ({ lat, lng }) => {
    if (!geocoder) return;

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const address = results[0].formatted_address;
        let city = "",
          state = "",
          country = "",
          postal_code = "";

        results[0].address_components.forEach((component) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (component.types.includes("country")) {
            country = component.long_name;
          }
          if (component.types.includes("postal_code")) {
            postal_code = component.long_name;
          }
        });

        setTempLocation({
          lat: Number(lat), // Ensure it's a number
          lng: Number(lng), // Ensure it's a number
          address: address,
          city: city,
          state: state,
          country: country,
          postal_code: postal_code,
        });
      } else {
        // Fallback if geocoding fails
        setTempLocation((prev) => ({
          ...prev,
          lat: Number(lat), // Ensure it's a number
          lng: Number(lng), // Ensure it's a number
          address: `Lat: ${Number(lat).toFixed(6)}, Lng: ${Number(lng).toFixed(6)}`,
        }));
      }
    });
  };

  // NEW: Search location on map
  const handleMapSearch = () => {
    if (!mapSearchQuery.trim() || !geocoder) return;

    geocoder.geocode({ address: mapSearchQuery }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        setMapCenter({ lat, lng });
        setMapZoom(15);
        handleMapClick({ lat, lng });
      } else {
        ShowToast("Location not found", Severty.ERROR);
      }
    });
  };

  // NEW: Use current location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter({ lat: latitude, lng: longitude });
          setMapZoom(15);
          handleMapClick({ lat: latitude, lng: longitude });
          // ShowToast("Location set to your current position", Severty.SUCCESS);
        },
        (error) => {
          console.log("Geolocation error:", error);
          ShowToast("Unable to get your location", Severty.ERROR);
        },
      );
    } else {
      ShowToast("Geolocation not supported", Severty.ERROR);
    }
  };

  // NEW: Save location - UPDATED with safe coordinate handling
  const saveLocation = () => {
    // Ensure coordinates are numbers or use defaults
    const safeLat =
      tempLocation.lat && typeof tempLocation.lat === "number"
        ? tempLocation.lat
        : null;
    const safeLng =
      tempLocation.lng && typeof tempLocation.lng === "number"
        ? tempLocation.lng
        : null;

    form.setFieldsValue({
      location: tempLocation.address || "",
      city: tempLocation.city || "",
      state: tempLocation.state || "",
      country: tempLocation.country || "",
      postal_code: tempLocation.postal_code || "",
      latitude: safeLat,
      longitude: safeLng,
    });
    setLocationModalVisible(false);
  };

  // Date range change handler
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      fetchUsers(1, pagination.pageSize, {
        searchKeyword,
        status: statusFilter,
        subscriptionStatus: subscriptionFilter,
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      });
    } else if (!dates) {
      // Clear date range
      setDateRange([]);
      fetchUsers(1, pagination.pageSize, {
        searchKeyword,
        status: statusFilter,
        subscriptionStatus: subscriptionFilter,
      });
    }
  };

  // Search & Filter
  const handleSearch = (kw) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const t = setTimeout(() => {
      setSearchKeyword(kw);
      const filters = {
        searchKeyword: kw,
        status: statusFilter,
        subscriptionStatus: subscriptionFilter,
      };

      // Add date range to filters if available
      if (dateRange && dateRange.length === 2) {
        filters.startDate = dateRange[0].format("YYYY-MM-DD");
        filters.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      fetchUsers(1, pagination.pageSize, filters);
    }, 500);
    setSearchTimeout(t);
  };

  const handleSearchChange = (e) => handleSearch(e.target.value);

  const handleSearchSubmit = (v) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchKeyword(v.keyword);
    const filters = {
      searchKeyword: v.keyword,
      status: statusFilter,
      subscriptionStatus: subscriptionFilter,
    };

    // Add date range to filters if available
    if (dateRange && dateRange.length === 2) {
      filters.startDate = dateRange[0].format("YYYY-MM-DD");
      filters.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    fetchUsers(1, pagination.pageSize, filters);
  };

  const handleStatusFilterChange = (v) => {
    setStatusFilter(v);
    const filters = {
      searchKeyword,
      status: v,
      subscriptionStatus: subscriptionFilter,
    };

    // Add date range to filters if available
    if (dateRange && dateRange.length === 2) {
      filters.startDate = dateRange[0].format("YYYY-MM-DD");
      filters.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    fetchUsers(1, pagination.pageSize, filters);
  };

  const handleSubscriptionFilterChange = (v) => {
    setSubscriptionFilter(v);
    const filters = {
      searchKeyword,
      status: statusFilter,
      subscriptionStatus: v,
    };

    // Add date range to filters if available
    if (dateRange && dateRange.length === 2) {
      filters.startDate = dateRange[0].format("YYYY-MM-DD");
      filters.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    fetchUsers(1, pagination.pageSize, filters);
  };

  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    setSubscriptionFilter("all");
    setDateRange([]);
    searchForm.resetFields();
    if (searchTimeout) clearTimeout(searchTimeout);
    fetchUsers();
  };

  // Date Helpers
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Extract date part from ISO string
      let datePart = dateString;
      if (typeof dateString === "string" && dateString.includes("T")) {
        datePart = dateString.split("T")[0];
      }

      const date = dayjs(datePart, "YYYY-MM-DD");
      if (!date.isValid()) {
        return "N/A";
      }
      return date.format("MMM D, YYYY");
    } catch (error) {
      return "N/A";
    }
  };

  // New function for table display - date only
  const formatTableDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      console.log("Raw date string:", dateString);

      // If it's an ISO string with timezone, extract just the date part (YYYY-MM-DD)
      if (typeof dateString === "string" && dateString.includes("T")) {
        const datePart = dateString.split("T")[0]; // Get "2025-12-28" from "2025-12-28T00:00:00+11:00"
        const date = dayjs(datePart);
        if (date.isValid()) {
          const formatted = date.format("MMM D, YYYY");
          console.log("Extracted date part:", datePart, "->", formatted);
          return formatted;
        }
      }

      // Fallback: try parsing the whole string
      const date = dayjs(dateString);
      if (date.isValid()) {
        return date.format("MMM D, YYYY");
      }

      return "N/A";
    } catch (error) {
      console.error("Date formatting error:", error, dateString);
      return "N/A";
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return "N/A";

      const diff = dayjs().diff(date, "milliseconds");
      const days = Math.floor(diff / 86400000);
      if (days === 0) return `${Math.floor(diff / 60000)} min ago`;
      if (days === 1) return "Yesterday";
      if (days < 7) return `${days} days ago`;
      if (days < 30) return `${Math.floor(days / 7)} wk ago`;
      return `${Math.floor(days / 30)} mo ago`;
    } catch (error) {
      console.error("Relative time error:", error);
      return "N/A";
    }
  };

  // Status conversion helpers
  const getStatusText = (status) => {
    return status === 1 || status === "Active" ? "Active" : "Inactive";
  };

  const getStatusBoolean = (status) => {
    return status === 1 || status === "Active";
  };

  // Get subscription color
  const getSubscriptionColor = (status) => {
    const option = subscriptionOptions.find((opt) => opt.value === status);
    return option ? option.color : "default";
  };

  // Check if user has used trial before
  const hasUsedTrialBefore = (user) => {
    if (!user) return false;

    // Check if trial end date exists and is in the past
    if (user.trial_end_date) {
      try {
        const trialEnd = dayjs(user.trial_end_date);
        if (trialEnd.isValid()) {
          return trialEnd.isBefore(dayjs());
        }
      } catch (error) {
        console.error("Trial date check error:", error);
      }
    }

    // Check if subscription was expired and had trial before
    if (user.subscription_status === "Expired" && user.trial_end_date) {
      return true;
    }

    return false;
  };

  // Open Subscription Modal
  const openSubscriptionModal = (user) => {
    setUpdatingUser(user);
    subscriptionForm.resetFields();

    const hasUsedTrial = hasUsedTrialBefore(user);

    // Set form values
    const formValues = {
      subscription_status: user.subscription_status || "Trial",
      subscription_period_unit: "months",
      subscription_period_value: 1,
    };

    // Helper function to safely set date values
    const setDateIfValid = (dateString, fieldName) => {
      if (dateString) {
        try {
          // Extract just the date part (YYYY-MM-DD) from ISO string
          let dateToParse = dateString;
          if (typeof dateString === "string" && dateString.includes("T")) {
            dateToParse = dateString.split("T")[0]; // Get "2025-12-28" from "2025-12-28T00:00:00+11:00"
          }

          const date = dayjs(dateToParse, "YYYY-MM-DD");
          if (date.isValid()) {
            formValues[fieldName] = date;
            console.log(
              `Setting ${fieldName}:`,
              dateString,
              "->",
              date.format("YYYY-MM-DD"),
            );
          }
        } catch (error) {
          console.error(`Error parsing ${fieldName}:`, error, dateString);
        }
      }
    };

    // Set dates if they exist
    setDateIfValid(user.subscription_start_date, "subscription_start_date");
    setDateIfValid(user.subscription_end_date, "subscription_end_date");
    setDateIfValid(user.trial_start_date, "trial_start_date");
    setDateIfValid(user.trial_end_date, "trial_end_date");

    // If subscription is active but no end date, calculate it
    // if (user.subscription_status === 'Active' && user.subscription_start_date && !user.subscription_end_date) {
    //   const startDate = dayjs(user.subscription_start_date);
    //   if (startDate.isValid()) {
    //     // Default 1 month from start
    //     formValues.subscription_end_date = startDate.add(1, 'month');
    //   }
    // }

    subscriptionForm.setFieldsValue(formValues);

    // Show/hide fields based on current status
    setShowPeriodFields(user.subscription_status === "Active");
    setShowTrialFields(user.subscription_status === "Trial");

    setSubscriptionModalVisible(true);

    // Return whether trial has been used before
    return hasUsedTrial;
  };

  // Handle subscription status change
  const handleSubscriptionStatusChange = (value) => {
    setShowPeriodFields(value === "Active");
    setShowTrialFields(value === "Trial");

    if (value === "Active") {
      // Set default start date to now if not set
      const currentValues = subscriptionForm.getFieldsValue();
      if (!currentValues.subscription_start_date) {
        subscriptionForm.setFieldsValue({
          subscription_start_date: dayjs(),
        });
        // Trigger end date calculation
        setTimeout(() => handlePeriodOrStartDateChange(), 100);
      }
    }

    // If selecting Trial and user has used it before, show warning
    if (value === "Trial" && updatingUser && hasUsedTrialBefore(updatingUser)) {
      ShowToast(
        "This user has already used their trial period. Trial cannot be selected again.",
        Severty.WARNING,
      );
    }
  };

  // Calculate end date based on start date and period
  const calculateEndDate = (startDate, periodValue, periodUnit) => {
    if (!startDate || !periodValue || !periodUnit) return null;

    try {
      let endDate;

      // Ensure startDate is a dayjs object
      const start = dayjs.isDayjs(startDate) ? startDate : dayjs(startDate);
      if (!start.isValid()) return null;

      const period = parseInt(periodValue);
      if (isNaN(period)) return null;

      switch (periodUnit) {
        case "days":
          endDate = start.add(period, "day");
          break;
        case "months":
          endDate = start.add(period, "month");
          break;
        case "years":
          endDate = start.add(period, "year");
          break;
        default:
          return null;
      }

      return endDate.isValid() ? endDate : null;
    } catch (error) {
      console.error("End date calculation error:", error);
      return null;
    }
  };

  // Handle period or start date change
  const handlePeriodOrStartDateChange = () => {
    const values = subscriptionForm.getFieldsValue();
    const {
      subscription_start_date,
      subscription_period_value,
      subscription_period_unit,
    } = values;

    if (
      subscription_start_date &&
      subscription_period_value &&
      subscription_period_unit
    ) {
      const endDate = calculateEndDate(
        subscription_start_date,
        subscription_period_value,
        subscription_period_unit,
      );
      if (endDate) {
        subscriptionForm.setFieldsValue({
          subscription_end_date: endDate,
        });
      }
    } else if (subscription_start_date && !values.subscription_end_date) {
      // If there's a start date but no end date, clear the end date field
      subscriptionForm.setFieldsValue({
        subscription_end_date: null,
      });
    }
  };

  // Update subscription
  const updateSubscription = (values) => {
    if (!updatingUser) return;

    // Check if trying to set Trial for user who has already used it
    if (
      values.subscription_status === "Trial" &&
      hasUsedTrialBefore(updatingUser)
    ) {
      ShowToast(
        "This user has already used their trial period. Trial cannot be selected again.",
        Severty.ERROR,
      );
      return;
    }

    setSubscriptionLoading(true);

    // Prepare payload
    const payload = {
      user_id: updatingUser.id,
      subscription_status: values.subscription_status,
    };

    // Helper function to format date in YYYY-MM-DD format (no time)
    const formatDateForApi = (date) => {
      if (!date) return null;
      try {
        if (dayjs.isDayjs(date)) {
          // Format as YYYY-MM-DD without time part
          return date.format("YYYY-MM-DD");
        }
        // If it's already a string, try to parse it
        const parsedDate = dayjs(date);
        if (parsedDate.isValid()) {
          return parsedDate.format("YYYY-MM-DD");
        }
        return null;
      } catch (error) {
        console.error("Date formatting error:", error);
        return null;
      }
    };

    // Add dates based on subscription status
    if (values.subscription_status === "Active") {
      payload.subscription_start_date = formatDateForApi(
        values.subscription_start_date,
      );
      payload.subscription_end_date = formatDateForApi(
        values.subscription_end_date,
      );
      payload.subscription_period_unit = values.subscription_period_value
        ? values.subscription_period_unit
        : null;
      payload.subscription_period_value = values.subscription_period_value;

      // If end date is not provided but period is, calculate it
      if (
        !payload.subscription_end_date &&
        payload.subscription_start_date &&
        values.subscription_period_value &&
        values.subscription_period_unit
      ) {
        const startDate = dayjs(payload.subscription_start_date);
        const periodValue = parseInt(values.subscription_period_value);

        if (values.subscription_period_unit === "months") {
          payload.subscription_end_date = startDate
            .add(periodValue, "month")
            .format("YYYY-MM-DD");
        } else if (values.subscription_period_unit === "years") {
          payload.subscription_end_date = startDate
            .add(periodValue, "year")
            .format("YYYY-MM-DD");
        }
      }
    } else if (values.subscription_status === "Trial") {
      payload.trial_start_date = formatDateForApi(values.trial_start_date);
      payload.trial_end_date = formatDateForApi(values.trial_end_date);

      // Set default trial dates if not provided
      if (!payload.trial_start_date) {
        payload.trial_start_date = dayjs().format("YYYY-MM-DD");
      }
      if (!payload.trial_end_date && payload.trial_start_date) {
        // Default 7-day trial
        payload.trial_end_date = dayjs(payload.trial_start_date)
          .add(7, "days")
          .format("YYYY-MM-DD");
      }
    }

    console.log("Sending subscription update payload:", payload);

    request({
      url: "/admin/update_subscription",
      method: "PUT",
      data: payload,
      onSuccess: (res) => {
        ShowToast(
          res.message || "Subscription updated successfully",
          Severty.SUCCESS,
        );
        setSubscriptionModalVisible(false);
        setSubscriptionLoading(false);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          subscriptionStatus: subscriptionFilter,
        });
      },
      onError: (e) => {
        const errorMessage =
          e?.response?.data?.error || "Failed to update subscription";
        ShowToast(errorMessage, Severty.ERROR);
        console.error("Subscription update error:", e?.response?.data);
        setSubscriptionLoading(false);
      },
    });
  };

  // Open Add/Edit Modal - UPDATED with safe coordinate parsing
  const openModal = (user = null) => {
    setEditingUser(user);
    form.resetFields();

    if (user) {
      // Safely parse coordinates
      const parseCoordinate = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const num = parseFloat(value);
        return isNaN(num) ? null : num;
      };

      form.setFieldsValue({
        ...user,
        status: getStatusBoolean(user.status),
        // Set location fields with safe parsing
        location: user.location || "",
        city: user.city || "",
        state: user.state || "",
        country: user.country || "",
        postal_code: user.postal_code || "",
        latitude: parseCoordinate(user.latitude),
        longitude: parseCoordinate(user.longitude),
        franchise_id: user.franchise_id || "",
      });
    } else {
      form.setFieldsValue({
        role: "Gym Owner",
        status: true,
        location: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        latitude: null,
        longitude: null,
        franchise_id: "",
      });
    }
    setModalVisible(true);
  };

  // Submit Form - UPDATED with location fields
  const onFinish = (values) => {
    console.log("Form Values:", values);
    setLoading(true);

    const payload = {
      ...values,
      role: "Gym Owner",
      status: editingUser
        ? editingUser.status
        : values.status
          ? "Active"
          : "Inactive",
      franchise_id: values.franchise_id || null,
      // Include location fields
      location: values.location || "",
      city: values.city || "",
      state: values.state || "",
      country: values.country || "",
      postal_code: values.postal_code || "",
      latitude: values.latitude || null,
      longitude: values.longitude || null,
    };

    const call = editingUser
      ? request({
          url: `/admin/update_user`,
          method: "PUT",
          data: { ...payload, id: editingUser.id },
          onSuccess: () => {
            ShowToast("Gym Owner updated", Severty.SUCCESS);
            fetchUsers(pagination.current, pagination.pageSize, {
              searchKeyword,
              status: statusFilter,
              subscriptionStatus: subscriptionFilter,
            });
            setModalVisible(false);
            setLoading(false);
          },
          onError: (e) => {
            ShowToast(
              e?.response?.data?.error || "Update failed",
              Severty.ERROR,
            );
            setLoading(false);
          },
        })
      : request({
          url: "/admin/create_user",
          method: "POST",
          data: payload,
          onSuccess: () => {
            ShowToast("Gym Owner created", Severty.SUCCESS);
            fetchUsers(pagination.current, pagination.pageSize, {
              searchKeyword,
              status: statusFilter,
              subscriptionStatus: subscriptionFilter,
            });
            setModalVisible(false);
            setLoading(false);
          },
          onError: (e) => {
            ShowToast(
              e?.response?.data?.error || "Create failed",
              Severty.ERROR,
            );
            setLoading(false);
          },
        });
  };

  // Toggle Status
  const toggleStatus = (rec) => {
    const newStatus = getStatusBoolean(rec.status) ? "Inactive" : "Active";
    request({
      url: `/admin/update_status`,
      method: "PUT",
      data: { id: rec.id, status: newStatus },
      onSuccess: () => {
        ShowToast("Status updated", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          subscriptionStatus: subscriptionFilter,
        });
      },
      onError: () => ShowToast("Failed", Severty.ERROR),
    });
  };

  // Delete Modal
  const openDeleteModal = (u) => {
    setDeletingUser(u);
    setDeleteModalVisible(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setDeletingUser(null);
    setDeleteLoading(false);
  };
  const handleDeleteUser = () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    request({
      url: `/admin/delete_user`,
      method: "DELETE",
      data: { id: deletingUser.id },
      onSuccess: () => {
        ShowToast("Gym Owner deleted", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          subscriptionStatus: subscriptionFilter,
        });
        closeDeleteModal();
      },
      onError: (e) => {
        ShowToast(e?.response?.data?.error || "Failed", Severty.ERROR);
        setDeleteLoading(false);
      },
    });
  };

  // Table Columns - UPDATED with location info
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (t) => <strong>{t}</strong>,
    },
    {
      title: "Image",
      dataIndex: "profile_image",
      key: "profile_image",
      render: (img) =>
        img ? <img src={img} width={50} height={50} alt="profile" /> : "-",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Gym Name",
      dataIndex: "franchise_id",
      key: "franchise_id",
      render: (franchiseId) =>
        franchiseId ? <Tag color="blue">{franchiseId}</Tag> : "N/A",
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          {record.city && <div>{record.city}</div>}
          {record.state && <div>{record.state}</div>}
          {record.country && <div>{record.country}</div>}
          {!record.city && !record.state && !record.country && "Not set"}
        </div>
      ),
    },
    {
      title: "Subscription Status",
      dataIndex: "subscription_status",
      key: "subscription_status",
      render: (status) => (
        <Tag color={getSubscriptionColor(status)}>{status || "N/A"}</Tag>
      ),
    },
    {
      title: "Subscription Dates",
      key: "subscription_dates",
      render: (_, record) => {
        const isActive = record.subscription_status === "Active";
        const isTrial = record.subscription_status === "Trial";

        if (isActive) {
          return (
            <div style={{ fontSize: "12px" }}>
              <div>
                Start: {formatTableDate(record.subscription_start_date)}
              </div>
              <div>End: {formatTableDate(record.subscription_end_date)}</div>
            </div>
          );
        } else if (isTrial) {
          return (
            <div style={{ fontSize: "12px" }}>
              <div>Start: {formatTableDate(record.trial_start_date)}</div>
              <div>End: {formatTableDate(record.trial_end_date)}</div>
            </div>
          );
        }
        return "-";
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (d) => (
        <div>
          <div style={{ fontSize: 12, color: "#666" }}>
            <CalendarOutlined style={{ marginRight: 4 }} /> {formatDate(d)}
          </div>
          <div style={{ fontSize: 11, color: "#999" }}>
            {getRelativeTime(d)}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s, r) => (
        <Switch
          checked={getStatusBoolean(s)}
          onChange={() => toggleStatus(r)}
          size="small"
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: "Actions",
      key: "action",
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="Edit Subscription">
            <Button
              size="small"
              icon={<DollarCircleOutlined />}
              onClick={() => {
                const hasUsedTrial = openSubscriptionModal(r);
                if (hasUsedTrial && r.subscription_status === "Trial") {
                  ShowToast(
                    "This user has already used their trial period.",
                    Severty.WARNING,
                  );
                }
              }}
              ghost
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openModal(r)}
            />
          </Tooltip>
          <Tooltip title="View">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => fetchUserDetails(r.id)}
            />
          </Tooltip>
          <Tooltip title="Shadow View">
            <Button
              size="small"
              icon={<AliwangwangOutlined />}
              onClick={() => handleVendorDashboard(r.id)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Manage Gym Owner
            </Title>
          </Col>
          <Col>
            <Button type="primary" onClick={() => openModal()}>
              Add New Gym Owner
            </Button>
          </Col>
        </Row>

        {/* Search & Filter */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={handleSearchSubmit}
            initialValues={{
              keyword: "",
              status: "all",
              subscription_status: "all",
            }}
          >
            <Row gutter={[16, 16]} align="end">
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="keyword" style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="Name or email..."
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={handleSearchChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item name="status" style={{ marginBottom: 0 }}>
                  <Select
                    onChange={handleStatusFilterChange}
                    style={{ width: "100%" }}
                    placeholder="Status"
                  >
                    <Option value="all">All Status</Option>
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item
                  name="subscription_status"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    onChange={handleSubscriptionFilterChange}
                    style={{ width: "100%" }}
                    placeholder="Subscription"
                  >
                    <Option value="all">All Subscription</Option>
                    {subscriptionOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item style={{ marginBottom: 0 }}>
                  <RangePicker
                    style={{ width: "100%" }}
                    placeholder={["Start Date", "End Date"]}
                    onChange={handleDateRangeChange}
                    value={dateRange}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={3}>
                <Button
                  style={{ width: "100%" }}
                  type="primary"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* Table */}
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={pagination}
            onChange={(p) => {
              const filters = {
                searchKeyword,
                status: statusFilter,
                subscriptionStatus: subscriptionFilter,
              };

              if (dateRange && dateRange.length === 2) {
                filters.startDate = dateRange[0].format("YYYY-MM-DD");
                filters.endDate = dateRange[1].format("YYYY-MM-DD");
              }

              fetchUsers(p.current, p.pageSize, filters);
            }}
            size="small"
          />
        </div>
      </Card>

      {/* LOCATION SELECTION MODAL - UPDATED with safe coordinate display */}
      <Modal
        open={locationModalVisible}
        onCancel={() => setLocationModalVisible(false)}
        onOk={saveLocation}
        width={800}
        okText="Save Location"
        cancelText="Cancel"
      >
        <Title level={4}>Select Gym Location</Title>

        <div style={{ marginBottom: 16 }}>
          <Row gutter={8}>
            <Col flex="auto">
              <Input
                placeholder="Search for a location..."
                value={mapSearchQuery}
                onChange={(e) => setMapSearchQuery(e.target.value)}
                prefix={<SearchOutlined />}
                onPressEnter={handleMapSearch}
              />
            </Col>
            <Col>
              <Button
                type="default"
                onClick={handleUseCurrentLocation}
                icon={<AimOutlined />}
              >
                Current Location
              </Button>
            </Col>
          </Row>
        </div>

        <div
          style={{
            height: "400px",
            width: "100%",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <GoogleMapReact
            bootstrapURLKeys={{
              key: googleMapsApiKey,
              libraries: ["places", "geometry"],
            }}
            center={mapCenter}
            zoom={mapZoom}
            onClick={handleMapClick}
            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
            options={{
              zoomControl: true,
              mapTypeControl: true,
              streetViewControl: false,
              fullscreenControl: true,
              scrollwheel: true,
              draggable: true,
            }}
          >
            {tempLocation.lat && tempLocation.lng && (
              <MapMarker
                lat={tempLocation.lat}
                lng={tempLocation.lng}
                text="Gym Location"
              />
            )}
          </GoogleMapReact>
        </div>

        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: "#f6f6f6",
            borderRadius: 6,
          }}
        >
          <Title level={5}>Selected Location Details</Title>
          <p>
            <strong>Address:</strong>{" "}
            {tempLocation.address || "Click on map to select"}
          </p>
          <p>
            <strong>Coordinates:</strong>{" "}
            {tempLocation.lat && tempLocation.lng
              ? `${typeof tempLocation.lat === "number" ? tempLocation.lat.toFixed(6) : tempLocation.lat}, ${typeof tempLocation.lng === "number" ? tempLocation.lng.toFixed(6) : tempLocation.lng}`
              : "Not selected"}
          </p>
          <Row gutter={16}>
            <Col span={6}>
              <strong>City:</strong> {tempLocation.city || "-"}
            </Col>
            <Col span={6}>
              <strong>State:</strong> {tempLocation.state || "-"}
            </Col>
            <Col span={6}>
              <strong>Country:</strong> {tempLocation.country || "-"}
            </Col>
            <Col span={6}>
              <strong>Postal Code:</strong> {tempLocation.postal_code || "-"}
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Add/Edit Modal - UPDATED with location fields */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Title level={3}>
          {editingUser ? "Edit Gym Owner" : "Add New Gym Owner"}
        </Title>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Enter name", whitespace: true },
            ]}
          >
            <Input placeholder="Gym Owner name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                type: "email",
                message: "Please enter a valid email!",
                whitespace: true,
              },
            ]}
          >
            <Input placeholder="email@example.com" disabled={!!editingUser} />
          </Form.Item>

          <Form.Item
            label="Gym Name"
            name="franchise_id"
            rules={[
              {
                required: true,
                message: "Please enter Gym Name",
                whitespace: true,
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\d+$/.test(value.trim())) {
                    return Promise.reject(
                      new Error("Gym Name cannot be only numbers"),
                    );
                  }
                  if (value.trim().length < 2) {
                    return Promise.reject(
                      new Error("Gym Name must be at least 2 characters"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter Gym Name" type="text" />
          </Form.Item>

          {/* NEW: Location Selection */}
          <Form.Item label="Gym Location">
            <Button
              type="dashed"
              icon={<EnvironmentOutlined />}
              onClick={openLocationModal}
              block
            >
              Select Gym Location on Map
            </Button>
          </Form.Item>

          {/* NEW: Location Fields */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Input placeholder="City" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="State">
                <Input placeholder="State" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country" label="Country">
                <Input placeholder="Country" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="postal_code" label="Postal Code">
                <Input placeholder="Postal Code" disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* Hidden location fields */}
          <Form.Item name="location" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="latitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Role" name="role">
            <Input
              value="Gym Owner"
              disabled
              style={{ background: "#f0f0f0" }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {editingUser ? "Update Gym Owner" : "Create Gym Owner"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Subscription Update Modal */}
      <Modal
        open={subscriptionModalVisible}
        onCancel={() => setSubscriptionModalVisible(false)}
        onOk={() => subscriptionForm.submit()}
        confirmLoading={subscriptionLoading}
        width={600}
        okText="Update Subscription"
        cancelText="Cancel"
      >
        <Title level={3}>Update Subscription</Title>
        {updatingUser && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                padding: "12px",
                background: "rgb(48 48 48)",
                borderRadius: "6px",
                marginBottom: 12,
                border: "1px solid #40424a",
              }}
            >
              <strong>User:</strong> {updatingUser.name} ({updatingUser.email})
              {/* Show current subscription dates */}
              {updatingUser.subscription_status === "Active" && (
                <div style={{ marginTop: 8, fontSize: "12px", color: "#ccc" }}>
                  <strong>Current:</strong>{" "}
                  {formatDate(updatingUser.subscription_start_date)} to{" "}
                  {formatDate(updatingUser.subscription_end_date)}
                </div>
              )}
              {updatingUser.subscription_status === "Trial" && (
                <div style={{ marginTop: 8, fontSize: "12px", color: "#ccc" }}>
                  <strong>Current Trial:</strong>{" "}
                  {formatDate(updatingUser.trial_start_date)} to{" "}
                  {formatDate(updatingUser.trial_end_date)}
                </div>
              )}
            </div>

            {/* Warning for trial period */}
            {updatingUser.subscription_status === "Trial" &&
              hasUsedTrialBefore(updatingUser) && (
                <Alert
                  message="Trial Period Already Used"
                  description="This user has already used their trial period. Trial cannot be selected again."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
          </div>
        )}

        <Form
          form={subscriptionForm}
          layout="vertical"
          onFinish={updateSubscription}
          onValuesChange={(changedValues) => {
            if (changedValues.subscription_status) {
              handleSubscriptionStatusChange(changedValues.subscription_status);
            }
            if (
              changedValues.subscription_start_date ||
              changedValues.subscription_period_value ||
              changedValues.subscription_period_unit
            ) {
              handlePeriodOrStartDateChange();
            }
          }}
        >
          <Form.Item
            label="Subscription Status"
            name="subscription_status"
            rules={[
              { required: true, message: "Please select subscription status" },
            ]}
          >
            <Select
              placeholder="Select status"
              onChange={handleSubscriptionStatusChange}
            >
              {subscriptionOptions.map((opt) => (
                <Option
                  key={opt.value}
                  value={opt.value}
                  disabled={
                    opt.value === "Trial" &&
                    updatingUser &&
                    hasUsedTrialBefore(updatingUser)
                  }
                >
                  <Tag color={opt.color}>{opt.label}</Tag>
                  {opt.value === "Trial" &&
                    updatingUser &&
                    hasUsedTrialBefore(updatingUser) &&
                    " (Already Used)"}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Active Subscription Fields */}
          {showPeriodFields && (
            <>
              <Form.Item
                label="Subscription Start Date"
                name="subscription_start_date"
                rules={[
                  { required: true, message: "Please select start date" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  onChange={handlePeriodOrStartDateChange}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Period Value"
                    name="subscription_period_value"
                    rules={[
                      { required: true, message: "Please enter period value" },
                    ]}
                  >
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="e.g., 1"
                      onChange={handlePeriodOrStartDateChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Period Unit"
                    name="subscription_period_unit"
                    rules={[
                      { required: true, message: "Please select period unit" },
                    ]}
                  >
                    <Select
                      placeholder="Select unit"
                      onChange={handlePeriodOrStartDateChange}
                    >
                      {periodUnitOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Subscription End Date (Auto-calculated)"
                name="subscription_end_date"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabled
                />
              </Form.Item>

              {/* Show preview of what will be sent */}
              {/* {subscriptionForm.getFieldValue('subscription_start_date') && (
          <Alert
            message="Update Preview"
            description={
              <div>
                <div>Start: {subscriptionForm.getFieldValue('subscription_start_date')?.format('MMM D, YYYY')}</div>
                <div>End: {subscriptionForm.getFieldValue('subscription_end_date')?.format('MMM D, YYYY') || 'Calculating...'}</div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )} */}
            </>
          )}

          {/* Trial Subscription Fields */}
          {showTrialFields && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Trial Start Date" name="trial_start_date">
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Trial End Date" name="trial_end_date">
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>

              {updatingUser && hasUsedTrialBefore(updatingUser) && (
                <Alert
                  message="Note"
                  description="Trial dates may be ignored by backend as this user has already used their trial period."
                  type="info"
                  showIcon
                />
              )}
            </>
          )}
        </Form>
      </Modal>
      {/* View Modal - UPDATED with safe coordinate display */}
      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        <Title level={3}>Gym Owner Details</Title>
        {viewingUser && (
          <div className="for-details-main">
            <Title level={4}>Basic Information</Title>
            <p>
              <strong>Name:</strong> {viewingUser.name}
            </p>
            <p>
              <strong>Email:</strong> {viewingUser.email}
            </p>
            <p>
              <strong>Gym Name:</strong>{" "}
              {viewingUser.franchise_id ? (
                <Tag color="blue">{viewingUser.franchise_id}</Tag>
              ) : (
                "N/A"
              )}
            </p>

            {/* NEW: Location Information */}
            <Title level={4} style={{ marginTop: 20 }}>
              Location Information
            </Title>
            <p>
              <strong>Address:</strong> {viewingUser.location || "N/A"}
            </p>
            <Row gutter={16}>
              <Col span={8}>
                <strong>City:</strong> {viewingUser.city || "N/A"}
              </Col>
              <Col span={8}>
                <strong>State:</strong> {viewingUser.state || "N/A"}
              </Col>
              <Col span={8}>
                <strong>Country:</strong> {viewingUser.country || "N/A"}
              </Col>
            </Row>
            <p>
              <strong>Postal Code:</strong> {viewingUser.postal_code || "N/A"}
            </p>
            {viewingUser.latitude && viewingUser.longitude && (
              <p>
                <strong>Coordinates:</strong>{" "}
                {typeof viewingUser.latitude === "number"
                  ? viewingUser.latitude.toFixed(6)
                  : viewingUser.latitude}
                ,{" "}
                {typeof viewingUser.longitude === "number"
                  ? viewingUser.longitude.toFixed(6)
                  : viewingUser.longitude}
              </p>
            )}

            <p>
              <strong>Phone:</strong> {viewingUser.phone_number || "N/A"}
            </p>
            <p>
              <strong>Subscription Status:</strong>{" "}
              <Tag
                color={getSubscriptionColor(viewingUser.subscription_status)}
              >
                {viewingUser.subscription_status || "N/A"}
              </Tag>
            </p>
            {viewingUser.subscription_status === "Active" && (
              <>
                <p>
                  <strong>Subscription Start:</strong>{" "}
                  {formatDate(viewingUser.subscription_start_date)}
                </p>
                <p>
                  <strong>Subscription End:</strong>{" "}
                  {formatDate(viewingUser.subscription_end_date)}
                </p>
              </>
            )}
            {viewingUser.subscription_status === "Trial" && (
              <>
                <p>
                  <strong>Trial Start:</strong>{" "}
                  {formatDate(viewingUser.trial_start_date)}
                </p>
                <p>
                  <strong>Trial End:</strong>{" "}
                  {formatDate(viewingUser.trial_end_date)}
                </p>
              </>
            )}
            <p>
              <strong>Created:</strong> {formatDate(viewingUser.created_at)}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <Tag
                color={getStatusBoolean(viewingUser.status) ? "green" : "red"}
              >
                {getStatusText(viewingUser.status)}
              </Tag>
            </p>
          </div>
        )}
      </Modal>

      {/* Delete Modal - UPDATED with location info */}
      <Modal
        open={deleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={deleteLoading}
            onClick={handleDeleteUser}
          >
            Delete Gym Owner
          </Button>,
        ]}
      >
        <div className="modal_header">
          <ExclamationCircleFilled
            style={{ color: "#faad14", marginRight: 8 }}
          />
          Delete Gym Owner
          <p>Are you sure you want to delete this gym Owner?</p>
        </div>
        {deletingUser && (
          <div className="for-details-main">
            <strong className="mb-3">Gym Owner Details:</strong>
            <p>
              <strong>Name:</strong> {deletingUser.name}
            </p>
            <p>
              <strong> Email: </strong> {deletingUser.email}
            </p>
            <p>
              <strong> Gym NAME: </strong> {deletingUser.franchise_id || "N/A"}
            </p>
            <p>
              <strong> Location: </strong> {deletingUser.location || "N/A"}
            </p>
            <p>
              <strong> City: </strong> {deletingUser.city || "N/A"}
            </p>
            <p>
              <strong> State: </strong> {deletingUser.state || "N/A"}
            </p>
            <p>
              <strong> Subscription Status: </strong>
              <Tag
                color={getSubscriptionColor(deletingUser.subscription_status)}
              >
                {deletingUser.subscription_status || "N/A"}
              </Tag>
            </p>
            <p>
              <strong> Status: </strong> {getStatusText(deletingUser.status)}
            </p>
            <p>
              <strong> Created: </strong> {formatDate(deletingUser.created_at)}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Vendors;
